import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";

interface Layer {
  name: string;
  type: "mask" | "overlay" | string;
}

interface LayerControlsTableProps {
  layers: Layer[];
  enabledLayers: Record<string, boolean>;
  layerOpacity: Record<string, number>;
  layerBlendMode: Record<string, string>;
  layerDescription: Record<string, string>;
  onToggleLayer: (layerName: string) => void;
  onOpacityChange: (layerName: string, opacity: number) => void;
  onBlendModeChange: (layerName: string, mode: string) => void;
  title?: string;
  isMaskLayer?: boolean;
}

/** CSS mix-blend-mode values; `label` is a translation key */
const BLEND_MODES: { value: string; label: TranslationKey }[] = [
  { value: "normal", label: "detail.blend.normal" },
  { value: "multiply", label: "detail.blend.multiply" },
  { value: "screen", label: "detail.blend.screen" },
  { value: "overlay", label: "detail.blend.overlay" },
  { value: "darken", label: "detail.blend.darken" },
  { value: "lighten", label: "detail.blend.lighten" },
  { value: "color-dodge", label: "detail.blend.colorDodge" },
  { value: "color-burn", label: "detail.blend.colorBurn" },
  { value: "hard-light", label: "detail.blend.hardLight" },
  { value: "soft-light", label: "detail.blend.softLight" },
  { value: "difference", label: "detail.blend.difference" },
  { value: "exclusion", label: "detail.blend.exclusion" },
  { value: "hue", label: "detail.blend.hue" },
  { value: "saturation", label: "detail.blend.saturation" },
  { value: "color", label: "detail.blend.color" },
  { value: "luminosity", label: "detail.blend.luminosity" },
];

export function LayerControlsTable({
  layers,
  enabledLayers,
  layerOpacity,
  layerBlendMode,
  layerDescription,
  onToggleLayer,
  onOpacityChange,
  onBlendModeChange,
  title,
  isMaskLayer = false,
}: LayerControlsTableProps) {
  const { t } = useLocalization();
  if (layers.length === 0) return null;

  return (
    <div className="mb-4 last:mb-0">
      {title && (
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </div>
      )}
      <ul className="divide-y rounded-lg border">
        {layers.map((layer, index) => (
          <li
            key={index}
            className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2 text-sm"
          >
            <div className="min-w-0 flex-1 basis-40">
              <div className="truncate font-mono text-xs">{layer.name}</div>
              {layerDescription[layer.name] && (
                <div className="truncate text-xs text-muted-foreground">
                  {layerDescription[layer.name]}
                </div>
              )}
            </div>
            <Switch
              checked={!!enabledLayers[layer.name]}
              onCheckedChange={() => onToggleLayer(layer.name)}
              aria-label={t("detail.layers.show", { name: layer.name })}
            />
            <Slider
              className="w-28"
              min={0}
              max={100}
              step={1}
              defaultValue={[layerOpacity[layer.name] || 100]}
              onValueChange={([value]) => onOpacityChange(layer.name, value)}
              aria-label={t("detail.layers.opacity", { name: layer.name })}
            />
            {isMaskLayer ? (
              <span className="w-[140px] text-xs italic text-muted-foreground">{t("detail.layers.mask")}</span>
            ) : (
              <Select
                value={layerBlendMode[layer.name] || "normal"}
                onValueChange={(value) => onBlendModeChange(layer.name, value)}
              >
                <SelectTrigger className="h-8 w-[140px]">
                  <SelectValue placeholder={t("detail.layers.blendMode")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>{t("detail.layers.blendModes")}</SelectLabel>
                    {BLEND_MODES.map(({ value, label }) => (
                      <SelectItem key={value} value={value}>
                        {t(label)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
